// ─── Types ───────────────────────────────────────────────────────────────────

export interface Patent {
  id: string;
  step: number;
  label: string;
  desc: string;
  detail: string;
}

export interface MetricsSnapshot {
  surgeons: number;
  facilities: number;
  ors: number;
  profiles: number;
  recorded: number;
  inquiries: number;
  /**
   * Subset of `inquiries` raised by internal `@exex.ai` staff. Reported, not
   * deducted — `inquiries` counts them. Not shown on the card; it reaches users
   * through the JSON export only.
   */
  internalInquiries: number;
  supported: number;
  staff: number;
}

export interface Milestone {
  label: string;
  current: number;
  target: number;
  weight: number;
  prefix?: string;
  suffix?: string;
  format: 'currency' | 'decimal' | 'number' | 'percent';
}

export interface Surgeon {
  name: string;
  specialty: string;
  profiles: number;
  recorded: number;
}

export interface Facility {
  id: string;
  name: string;
  region: 'US' | 'UK';
  city: string;
  ors: number;
  status: string;
  launchDate: string;
  specialties: string[];
  surgeons: Surgeon[];
}

export interface Region {
  key: string;
  label: string;
}

export type ProductPlatform = 'WEB' | 'iOS' | 'visionOS';

export interface ProductIntelItem {
  label: string;
  value: string;
}

export interface ProductIntel {
  name: string;
  platform: ProductPlatform;
  items: ProductIntelItem[];
}

export type TimeFilterKey = 'all' | '90d' | '30d';

/** Which facilities the Operational Metrics cards cover. `withLegacy` is the default. */
export type MetricScopeKey = 'commercial' | 'withLegacy';

// ─── SVG Color Constants ─────────────────────────────────────────────────────
// SVG gradient `stopColor` values don't reliably support CSS custom properties,
// so we mirror the token values as JS constants here.

export const SVG_COLORS = {
  accent: '#0FB7D1',
  green: '#00E676',
  textDim: '#6B7A8D',
  textBright: '#FFFFFF',
  text: '#E0E6ED',
  panelBorder: 'rgba(15,183,209,0.15)',
  accentDim: 'rgba(15,183,209,0.4)',
  bg: '#030810',
} as const;

// ─── Data ────────────────────────────────────────────────────────────────────

// Surgical-inquiry tracking only began on this date, so the all-time inquiries
// count covers from here — not the platform's full history.
export const SURGICAL_INQUIRIES_SINCE_ISO = '2026-06-03';
export const SURGICAL_INQUIRIES_SINCE_LABEL = 'June 3, 2026';

export const PATENTS: Patent[] = [
  {
    id: 'EE-PAT24',
    step: 1,
    label: 'Scan OR',
    desc: 'Spatial capture via LiDAR and cameras',
    detail:
      'Captures a full 3D spatial map of the operating room using LiDAR and depth cameras, creating a digital twin of the physical space for asset tracking and layout planning.',
  },
  {
    id: 'EE-PAT24',
    step: 2,
    label: 'Plan Setup',
    desc: '3D Surgical Profile & asset placement',
    detail:
      'Generates surgeon-approved 3D Surgical Profiles with precise equipment placement, tray positioning, and resource allocation mapped to the scanned room geometry.',
  },
  {
    id: 'EE-PAT24',
    step: 3,
    label: 'Guide',
    desc: 'Spatial overlays drive repeatable setup',
    detail:
      'Projects spatial computing overlays into the OR environment, guiding staff through each setup step with positional accuracy to ensure repeatable, surgeon-approved configurations.',
  },
  {
    id: 'EE-PAT06',
    step: 4,
    label: 'Monitor',
    desc: 'Capture deviations & usage signals',
    detail:
      'Continuously captures workflow deviations, tool usage patterns, consumable tracking, and time-stamped operational signals throughout the surgical procedure.',
  },
  {
    id: 'EE-PAT08',
    step: 5,
    label: 'Coach',
    desc: 'Remote proctoring & instruction',
    detail:
      'Enables experienced surgeons to remotely proctor and instruct staff in real-time through spatial computing interfaces, bridging expertise gaps across facilities.',
  },
  {
    id: 'EE-PAT09',
    step: 6,
    label: 'Remote Optimize',
    desc: 'Intra-op coaching + outcomes loop',
    detail:
      'Connects intra-operative coaching signals with post-operative outcomes data, creating a continuous feedback loop that ties technology usage to performance metrics.',
  },
  {
    id: 'EE-PAT06-CON + EE-PAT28',
    step: 7,
    label: 'Learn / Codify',
    desc: 'ML compares vs. gold standard',
    detail:
      'Machine learning compares each procedure against the gold-standard Surgical Profile, identifies optimization opportunities, and automatically updates procedure documentation.',
  },
  {
    id: 'EE-PAT07',
    step: 8,
    label: 'Harmonize',
    desc: 'Multi-surgeon adaptive templates',
    detail:
      'Synthesizes workflow patterns across multiple surgeons to create adaptive templates that balance individual preferences with institutional best practices.',
  },
];

// Mirrors the backend's FALLBACK_METRICS: these totals aren't attributed to
// members, so `internalInquiries` is 0 — as with the empty per-facility rows the
// fallback ships, there is nothing to attribute.
export const METRICS_DATA: Record<TimeFilterKey, MetricsSnapshot> = {
  all: {
    surgeons: 277,
    facilities: 4,
    ors: 36,
    profiles: 1477,
    recorded: 2709,
    inquiries: 6800,
    internalInquiries: 0,
    supported: 5000,
    staff: 472,
  },
  '90d': {
    surgeons: 112,
    facilities: 4,
    ors: 36,
    profiles: 489,
    recorded: 1105,
    inquiries: 2760,
    internalInquiries: 0,
    supported: 2100,
    staff: 198,
  },
  '30d': {
    surgeons: 41,
    facilities: 4,
    ors: 36,
    profiles: 167,
    recorded: 380,
    inquiries: 950,
    internalInquiries: 0,
    supported: 1250,
    staff: 72,
  },
};

export const MILESTONES: Milestone[] = [
  { label: 'ARR', current: 570000, target: 2000000, weight: 0.25, prefix: '$', format: 'currency' },
  {
    label: 'Quarter on Quarter Growth',
    current: 12,
    target: 20,
    weight: 0.15,
    suffix: '%',
    format: 'percent',
  },
  { label: 'Facilities', current: 3, target: 10, weight: 0.1, format: 'number' },
  { label: 'Surgeons', current: 277, target: 500, weight: 0.15, format: 'number' },
  { label: 'Surgical Profiles', current: 1477, target: 2500, weight: 0.15, format: 'number' },
  { label: 'Recorded Procedures', current: 2709, target: 10000, weight: 0.1, format: 'number' },
  {
    label: 'Surgeries 30 Day Run Rate',
    current: 250,
    target: 3000,
    weight: 0.1,
    format: 'number',
  },
];

export const FACILITIES: Facility[] = [
  {
    id: 'smh',
    name: 'Sarasota Memorial Hospital',
    region: 'US',
    city: 'Sarasota, FL',
    ors: 18,
    status: 'ACTIVE',
    launchDate: '2024-09-15',
    specialties: ['Spine', 'Orthopedics', 'Neurosurgery', 'General Surgery'],
    surgeons: [
      { name: 'Dr. Robert Masson', specialty: 'Spine', profiles: 87, recorded: 312 },
      { name: 'Dr. Angela Rivera', specialty: 'Spine', profiles: 64, recorded: 198 },
      { name: 'Dr. James Thornton', specialty: 'Orthopedics', profiles: 52, recorded: 167 },
      { name: 'Dr. Priya Nair', specialty: 'Neurosurgery', profiles: 71, recorded: 224 },
      { name: 'Dr. Michael Chen', specialty: 'Spine', profiles: 43, recorded: 145 },
      { name: 'Dr. Laura Fitzgerald', specialty: 'General Surgery', profiles: 38, recorded: 112 },
      { name: 'Dr. David Kowalski', specialty: 'Orthopedics', profiles: 55, recorded: 189 },
      { name: 'Dr. Sarah Kim', specialty: 'Neurosurgery', profiles: 46, recorded: 134 },
      { name: 'Dr. Thomas Brennan', specialty: 'Spine', profiles: 61, recorded: 201 },
      { name: 'Dr. Maria Santos', specialty: 'General Surgery', profiles: 33, recorded: 98 },
      { name: 'Dr. Richard Hayes', specialty: 'Orthopedics', profiles: 49, recorded: 156 },
      { name: 'Dr. Emily Watson', specialty: 'Spine', profiles: 39, recorded: 121 },
      { name: 'Dr. Hassan Al-Rashid', specialty: 'Neurosurgery', profiles: 57, recorded: 178 },
      { name: 'Dr. Catherine Dupont', specialty: 'General Surgery', profiles: 29, recorded: 87 },
      { name: 'Dr. William Park', specialty: 'Orthopedics', profiles: 41, recorded: 131 },
    ],
  },
  {
    id: 'ahc',
    name: 'AdventHealth Carrollwood',
    region: 'US',
    city: 'Tampa, FL',
    ors: 12,
    status: 'ACTIVE',
    launchDate: '2025-03-22',
    specialties: ['Spine', 'Orthopedics', 'Pain Management'],
    surgeons: [
      { name: 'Dr. Steven Calloway', specialty: 'Spine', profiles: 58, recorded: 142 },
      { name: 'Dr. Nina Patel', specialty: 'Orthopedics', profiles: 44, recorded: 108 },
      { name: 'Dr. Gregory Monroe', specialty: 'Spine', profiles: 51, recorded: 137 },
      { name: 'Dr. Jessica Tran', specialty: 'Pain Management', profiles: 36, recorded: 89 },
      { name: 'Dr. Daniel Ortiz', specialty: 'Orthopedics', profiles: 47, recorded: 125 },
      { name: 'Dr. Rebecca Young', specialty: 'Spine', profiles: 42, recorded: 114 },
      { name: 'Dr. Christopher Blake', specialty: 'Pain Management', profiles: 31, recorded: 76 },
      { name: 'Dr. Amanda Foster', specialty: 'Orthopedics', profiles: 39, recorded: 97 },
      { name: 'Dr. Kevin Wright', specialty: 'Spine', profiles: 53, recorded: 148 },
      { name: 'Dr. Helen Choi', specialty: 'Pain Management', profiles: 28, recorded: 64 },
    ],
  },
  {
    id: 'crw',
    name: 'Cromwell Hospital',
    region: 'UK',
    city: 'London, UK',
    ors: 6,
    status: 'ACTIVE',
    launchDate: '2025-08-10',
    specialties: ['Spine', 'Orthopedics', 'Neurosurgery'],
    surgeons: [
      { name: 'Mr. Edward Harrington', specialty: 'Spine', profiles: 62, recorded: 94 },
      { name: 'Mr. James Ashworth', specialty: 'Orthopedics', profiles: 45, recorded: 72 },
      { name: 'Ms. Sophia Cartwright', specialty: 'Neurosurgery', profiles: 53, recorded: 81 },
      { name: 'Mr. Oliver Pennington', specialty: 'Spine', profiles: 41, recorded: 63 },
      { name: 'Ms. Charlotte Whitfield', specialty: 'Orthopedics', profiles: 37, recorded: 58 },
      { name: 'Mr. Benjamin Hale', specialty: 'Neurosurgery', profiles: 48, recorded: 74 },
      { name: 'Ms. Victoria Marsh', specialty: 'Spine', profiles: 34, recorded: 52 },
      { name: 'Mr. Alexander Reid', specialty: 'Orthopedics', profiles: 40, recorded: 61 },
    ],
  },
];

export const REGIONS: Region[] = [
  { key: 'all', label: 'WORLDWIDE' },
  { key: 'US', label: 'UNITED STATES' },
  { key: 'UK', label: 'UNITED KINGDOM' },
];

export const PRODUCT_INTEL: ProductIntel[] = [
  {
    name: 'CreatorX',
    platform: 'WEB',
    items: [
      { label: 'Version', value: 'v03.01.08' },
      { label: 'Last Deploy', value: '2026-01-02' },
      { label: 'Uptime', value: '99.95%' },
    ],
  },
  {
    name: 'ViewerX',
    platform: 'iOS',
    items: [
      { label: 'Version', value: 'v03.01.10' },
      { label: 'Last Release', value: '2026-01-08' },
      { label: 'Installs', value: '2,444' },
    ],
  },
  {
    name: 'ExperienceX',
    platform: 'visionOS',
    items: [
      { label: 'Version', value: 'v01.00.01' },
      { label: 'Last Release', value: '2025-06-09' },
    ],
  },
];
